import { useCallback, useEffect } from "react";
import {
	Vector2,
	Color,
	RepeatWrapping,
	MeshStandardMaterial,
} from "three";
import { debug } from "rtUtils/logger";
import { useConfigCloudName, useConfigModelUrl, useConfigProductConfig } from "./config";
import useAnalytics from "./useAnalytics";
import ANALYTICS_EVENTS from "../analyticsEvents";
import { issueModelProcessorRequest } from "../utils/arUtils";

const RESET_KEYWORD = "reset";

const SUPPORTED_MAP_TYPES = [
	{ type: "url", fn: "replaceMeshMap", mapName: "map" },
	{ type: "roughnessUrl", fn: "replaceMeshRoughnessMap", mapName: "roughnessMap" },
	{ type: "normalUrl", fn: "replaceMeshNormalMap", mapName: "normalMap" },
	{ type: "alphaUrl", fn: "replaceMeshAlphaMap", mapName: "alphaMap" },
	{ type: "aoUrl", fn: "replaceMeshAoMap", mapName: "aoMap" },
	{ type: "metalnessUrl", fn: "replaceMeshMetalnessMap", mapName: "metalnessMap" },
	{ type: "bumpUrl", fn: "replaceMeshBumpMap", mapName: "bumpMap" },
];

const SUPPORTED_MESH_PARAMS = ["visible"];

const storeMeshResetInfo = (mesh, config, createdMaterial) => {
	const matParams = config.materialParams || {};
	const meshParams = config.meshParams || {};

	const resetInfo = mesh.userData.d8sResetInfo = {
		createdMaterial,
		matParams: {},
		meshParams: {},
		maps: {},
	};

	Object.keys(matParams)
		.forEach((param) => {
			resetInfo.matParams[param] = mesh.material[param];
		});

	Object.keys(meshParams)
		.forEach((param) => {
			if (SUPPORTED_MESH_PARAMS.includes(param)) {
				resetInfo.meshParams[param] = mesh[param];
			}
		});

	SUPPORTED_MAP_TYPES.forEach(({ type, mapName }) => {
		if (config[type] || config[type] === false) {
			resetInfo.maps[mapName] = mesh.material[mapName];
		}
	});
};

const getColor = (val) => Array.isArray(val) ?
	new Color(...val) :
	new Color(parseInt(val, 16));

const applyChangesToMesh = (mesh, name, config, renderingApi) => {
	const txtParams = config.textureParams || {};
	const matParams = config.materialParams || {};
	const meshParams = config.meshParams || {};

	let noMaterial = false;

	if (!mesh.material) {
		debug("creating mesh material for textures!");
		noMaterial = true;

		mesh.material = new MeshStandardMaterial();
	}

	if (!mesh.userData?.d8sResetInfo) {
		mesh.userData = mesh.userData || {};
		storeMeshResetInfo(mesh, config, noMaterial);
	}

	const applyRepeat = (texture, val) => {
		if (val) {
			const valArr = Array.isArray(val) ? val : [val, val];
			texture.repeat.set(valArr[0], valArr[1]);
			texture.wrapS = RepeatWrapping;
			texture.wrapT = RepeatWrapping;
		}
	};

	SUPPORTED_MAP_TYPES.forEach(({ type, fn, mapName }) => {
		//false means remove the map
		if (config[type] || config[type] === false) {
			if (config[type] === RESET_KEYWORD) {
				resetSpecificMap(mesh, mapName);
			} else {
				renderingApi[fn](name, config[type], (mesh, material, texture) => {
					Object.entries(txtParams)
						.forEach(([key, val]) => {
							if (key === "repeat") {
								applyRepeat(texture, val);
							} else {
								texture[key] = val;
							}
						});
				});
			}
		}
	});

	Object.entries(matParams)
		.forEach(([key, val]) => {
			switch (key) {
				case "normalScale":
					val = new Vector2(val, val);
					break;
				case "opacity":
					const parsed = Number(matParams.opacity);
					val = isNaN(parsed) ? 1.0 : parsed;
					break;
				case "color":
					val = getColor(matParams.color);
					val.convertLinearToSRGB();
					break;
				case "emissive":
					val = getColor(matParams.emissive);
					val.convertLinearToSRGB();
					break;
			}

			mesh.material[key] = val;
		});

	Object.entries(meshParams)
		.forEach(([key, val]) => {
			if (SUPPORTED_MESH_PARAMS.includes(key)) {
				mesh[key] = val;
			}
		});
};

const resetSpecificMap = (mesh, mapName) => {
	let resetInfo = mesh.userData?.d8sResetInfo;

	if (Object.keys(resetInfo?.maps || {}).includes(mapName)) {
		mesh.material[mapName] = resetInfo.maps[mapName];
	}
};

const applyResetToMesh = (mesh) => {
	let resetInfo = mesh.userData?.d8sResetInfo;

	if (resetInfo) {
		if (resetInfo.createdMaterial) {
			mesh.material = null;
		} else {
			//restore material params
			Object.keys(resetInfo.matParams)
				.forEach((param) => {
					mesh.material[param] = resetInfo.matParams[param];
				});

			//restore maps
			Object.entries(resetInfo.maps)
				.forEach(([name, val]) => {
					mesh.material[name] = val;
				});
		}

		//restore mesh params
		Object.entries(resetInfo.meshParams)
			.forEach(([name, val]) => {
				mesh[name] = val;
			});

		//clear reset info
		mesh.userData.d8sResetInfo = undefined;
	}
};

const getAnalyticsData = (pcs) => {
	return {
		meshes: pcs.length,
		...pcs.reduce((res, { config }) => {
			SUPPORTED_MAP_TYPES.forEach(({ type}) => {
				if (config[type]) {
					res.urls +=1;
				}
			})

			if (config?.materialParams?.color) {
				res.color = true;
			}

			return res;
		}, { urls: 0, color: false }),
	}
};

const useProductConfigApplier = (getIsReady, renderingApi, productConfigs, triggerAnalyticsEvent = null) => {
	const isReady = getIsReady();

	useEffect(() => {
		if (isReady && renderingApi) {
			if (productConfigs?.length) {
				debug("applying product configuration(s) - ", productConfigs);

				productConfigs.forEach(({ mesh: meshName, config }) => {
					let mesh = renderingApi.getMesh(meshName);
					if (!mesh && config?.useText) {
						const textParent = renderingApi.getThree().scene.children.find(x => x.name == 'text_nodes');
						mesh = textParent.getObjectByName(meshName);
						mesh.userData.setText(config.text || "");
					}

					if (config[RESET_KEYWORD] === true) {
						applyResetToMesh(mesh);
					} else {
						applyChangesToMesh(mesh, meshName, config, renderingApi);
					}

					mesh.material.needsUpdate = true;
				});

				triggerAnalyticsEvent?.(ANALYTICS_EVENTS.PRODUCT_CONFIG, getAnalyticsData(productConfigs));
			}
		}
	}, [isReady, productConfigs, renderingApi]);
};

const useProductConfigApplierFromState = (getIsReady, renderingApi) => {
	const cloudName = useConfigCloudName();
	const productConfigs = useConfigProductConfig();
	const modelUrl = useConfigModelUrl();
	const { triggerAnalyticsEvent } = useAnalytics();

	const onApplied = useCallback((...args) => {
		triggerAnalyticsEvent(...args);
		issueModelProcessorRequest(modelUrl, productConfigs, cloudName);
	}, [productConfigs, modelUrl]);

	return useProductConfigApplier(getIsReady, renderingApi, productConfigs, onApplied);
};

export {
	useProductConfigApplierFromState,
	useProductConfigApplier,
};
